import React, { FC, Fragment, ReactNode } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  className?: string;
  position?: "right" | "left";
}

const Drawer: FC<DrawerProps> = ({
  isOpen,
  onClose,
  children,
  title,
  className = "",
  position = "right",
}) => {
  const isRight = position === "right";

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 dark:bg-black/60" />
        </Transition.Child>

        {/* Drawer panel */}
        <div
          className={`fixed inset-y-0 ${
            isRight ? "right-0" : "left-0"
          } flex`}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom={
              isRight ? "translate-x-full" : "-translate-x-full"
            }
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo={
              isRight ? "translate-x-full" : "-translate-x-full"
            }
          >
            <Dialog.Panel
              className={`w-full max-w-[400px] bg-white dark:bg-slate-900 shadow-xl flex flex-col h-full border-l border-slate-200 dark:border-slate-700 ${
                isRight ? "border-l" : "border-r"
              } ${className}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200 dark:border-slate-700">
                {title && (
                  <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {title}
                  </Dialog.Title>
                )}
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto overscroll-contain">
                {children}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Drawer;
